import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, AreaChart, Area, BarChart, Bar
} from "recharts";
import {
  Mic, MicOff, Activity, TrendingUp, FileText, Download,
  CheckCircle2, AlertTriangle, Clock, Users, RefreshCw,
  Loader2, Play, Square, Volume2, ChevronRight, ArrowUp,
  ArrowDown, Minus, Heart, Wind, ThermometerSun, Brain,
  Moon, Coffee, Pill, Bell, BellRing, Eye, X, Zap, ArrowRight
} from "lucide-react";

// HOPE Symptom Configuration
const HOPE_SYMPTOMS = [
  { key: "pain", label: "Pain", icon: Heart, color: "#ef4444" },
  { key: "dyspnea", label: "Dyspnea", icon: Wind, color: "#f97316" },
  { key: "nausea", label: "Nausea", icon: ThermometerSun, color: "#eab308" },
  { key: "anxiety", label: "Anxiety", icon: Brain, color: "#8b5cf6" },
  { key: "depression", label: "Depression", icon: Moon, color: "#6366f1" },
  { key: "drowsiness", label: "Drowsiness", icon: Coffee, color: "#14b8a6" },
  { key: "constipation", label: "Constipation", icon: Pill, color: "#ec4899" }
];

// Score severity colors
const getScoreColor = (score) => {
  if (score <= 2) return "text-green-600 bg-green-100";
  if (score <= 4) return "text-yellow-600 bg-yellow-100";
  if (score <= 6) return "text-orange-600 bg-orange-100";
  return "text-red-600 bg-red-100";
};

const getScoreLabel = (score) => {
  if (score === 0) return "None";
  if (score <= 2) return "Slight";
  if (score <= 4) return "Mild";
  if (score <= 6) return "Moderate";
  if (score <= 8) return "Severe";
  return "Extreme";
};

// Status Indicator Component
const StatusIndicator = ({ status }) => {
  const colors = {
    operational: "bg-green-500",
    degraded: "bg-yellow-500",
    issues: "bg-red-500",
    loading: "bg-slate-400 animate-pulse"
  };
  return <div className={`w-2 h-2 rounded-full ${colors[status] || colors.loading}`} />;
};

// Push-to-Talk Button Component
const PushToTalkButton = ({ onRecordingComplete, patientId, disabled }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      
      chunksRef.current = [];
      mediaRecorderRef.current = mediaRecorder;
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const reader = new FileReader();
        
        reader.onloadend = async () => {
          const base64Audio = reader.result.split(',')[1];
          setIsProcessing(true);
          
          try {
            const response = await api.post("/hope/voice-to-hope", {
              patient_id: patientId,
              audio_base64: base64Audio,
              audio_format: "webm"
            });
            
            onRecordingComplete(response.data);
            toast.success("Voice note processed successfully!");
          } catch (error) {
            console.error("Processing error:", error);
            toast.error(error.response?.data?.detail || "Failed to process voice note");
          } finally {
            setIsProcessing(false);
          }
        };
        
        reader.readAsDataURL(audioBlob);
      };
      
      mediaRecorder.start(100);
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);
      
    } catch (error) {
      console.error("Microphone error:", error);
      toast.error("Could not access microphone");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex flex-col items-center gap-4">
      <button
        onClick={isRecording ? stopRecording : startRecording}
        disabled={disabled || isProcessing}
        className={`
          relative w-32 h-32 rounded-full flex items-center justify-center
          transition-all duration-300 shadow-lg
          ${isRecording 
            ? 'bg-red-500 hover:bg-red-600 animate-pulse scale-110' 
            : 'bg-teal-600 hover:bg-teal-700'}
          ${disabled || isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        data-testid="push-to-talk-btn"
      >
        {isProcessing ? (
          <Loader2 className="w-12 h-12 text-white animate-spin" />
        ) : isRecording ? (
          <>
            <Square className="w-12 h-12 text-white" />
            <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-xs text-white font-mono bg-red-600 px-2 py-1 rounded">
              {formatTime(recordingTime)}
            </span>
          </>
        ) : (
          <Mic className="w-12 h-12 text-white" />
        )}
      </button>
      
      <p className="text-sm text-muted-foreground text-center">
        {isProcessing ? "Processing voice note..." : 
         isRecording ? "Recording... Tap to stop" : 
         "Tap to start recording"}
      </p>
    </div>
  );
};

// SOT Chart Component
const SOTChart = ({ data, selectedSymptoms }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-[300px] flex items-center justify-center text-muted-foreground">
        No trend data available
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis 
          dataKey="date" 
          tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          stroke="#6b7280"
        />
        <YAxis domain={[0, 9]} ticks={[0, 3, 6, 9]} stroke="#6b7280" />
        <Tooltip 
          labelFormatter={(date) => new Date(date).toLocaleDateString()}
          contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb' }}
        />
        <Legend />
        {HOPE_SYMPTOMS.filter(s => selectedSymptoms.includes(s.key)).map(symptom => (
          <Line
            key={symptom.key}
            type="monotone"
            dataKey={symptom.key}
            name={symptom.label}
            stroke={symptom.color}
            strokeWidth={2}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

// Symptom Score Card
const SymptomScoreCard = ({ symptom, score, change, baseline, onScoreChange, editable }) => {
  const Icon = symptom.icon;
  const changeIcon = change > 0 ? ArrowUp : change < 0 ? ArrowDown : Minus;
  const changeColor = change > 0 ? "text-red-500" : change < 0 ? "text-green-500" : "text-gray-400";
  
  return (
    <div className="p-4 border rounded-lg bg-white hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg" style={{ backgroundColor: `${symptom.color}20` }}>
            <Icon className="w-4 h-4" style={{ color: symptom.color }} />
          </div>
          <span className="font-medium">{symptom.label}</span>
        </div>
        {change !== undefined && change !== 0 && (
          <div className={`flex items-center gap-1 text-xs ${changeColor}`}>
            {React.createElement(changeIcon, { className: "w-3 h-3" })}
            {Math.abs(change)}
          </div>
        )}
      </div>
      
      {editable ? (
        <div className="space-y-2">
          <Slider
            value={[score]}
            onValueChange={([val]) => onScoreChange(symptom.key, val)}
            max={9}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>0 (None)</span>
            <span className={`font-medium ${getScoreColor(score)} px-2 py-0.5 rounded`}>
              {score} - {getScoreLabel(score)}
            </span>
            <span>9 (Extreme)</span>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div className={`text-2xl font-bold px-3 py-1 rounded ${getScoreColor(score)}`}>
            {score}
          </div>
          <div className="text-right">
            <p className="text-sm font-medium">{getScoreLabel(score)}</p>
            {baseline !== undefined && (
              <p className="text-xs text-muted-foreground">Baseline: {baseline}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Main HOPE Dashboard Component
export default function HOPEDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("record");
  const [loading, setLoading] = useState(true);
  const [systemStatus, setSystemStatus] = useState("loading");
  
  // Dashboard data
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState("");
  const [patients, setPatients] = useState([]);
  const [latestObservation, setLatestObservation] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [selectedSymptoms, setSelectedSymptoms] = useState(["pain", "dyspnea", "anxiety"]);
  
  // Manual entry
  const [manualScores, setManualScores] = useState(
    HOPE_SYMPTOMS.reduce((acc, s) => ({ ...acc, [s.key]: 0 }), {})
  );
  const [manualNotes, setManualNotes] = useState("");
  
  // Voice recording result
  const [recordingResult, setRecordingResult] = useState(null);
  
  // Trend Alerts
  const [trendAlerts, setTrendAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);

  // Load initial data
  useEffect(() => {
    loadDashboardData();
    loadPatients();
    checkSystemStatus();
    loadTrendAlerts();
  }, []);

  // Load patient data when selected
  useEffect(() => {
    if (selectedPatient) {
      loadPatientData(selectedPatient);
    }
  }, [selectedPatient]);

  const checkSystemStatus = async () => {
    try {
      const response = await api.get("/hope/health");
      setSystemStatus(response.data.status || "operational");
    } catch (error) {
      setSystemStatus("issues");
    }
  };

  const loadDashboardData = async () => {
    try {
      const response = await api.get("/hope/dashboard/summary");
      setDashboardSummary(response.data);
    } catch (error) {
      console.error("Dashboard load error:", error);
      // Use mock data
      setDashboardSummary({
        today_observations: 12,
        pending_verification: 3,
        high_symptom_alerts: 5,
        patients_tracked: 47,
        active_trend_alerts: 3,
        symptom_averages: HOPE_SYMPTOMS.reduce((acc, s) => ({
          ...acc,
          [s.key]: { average: Math.random() * 5, observations: Math.floor(Math.random() * 50) }
        }), {})
      });
    } finally {
      setLoading(false);
    }
  };

  const loadTrendAlerts = async () => {
    setAlertsLoading(true);
    try {
      const response = await api.get("/hope/trend-alerts?acknowledged=false");
      setTrendAlerts(response.data.alerts || []);
    } catch (error) {
      console.error("Trend alerts load error:", error);
      // Use mock alerts
      setTrendAlerts([
        {
          alert_id: "1",
          patient_id: "P001",
          patient_name: "John Smith",
          symptom_type: "pain",
          alert_type: "upward_trend",
          severity: "critical",
          current_score: 7,
          previous_scores: [4, 5, 6],
          trend_direction: "increasing",
          percentage_change: 75,
          message: "Pain showing concerning upward trend: 4 → 7 over 4 visits",
          recommended_action: "Review pain management plan. Consider medication adjustment.",
          created_at: new Date().toISOString(),
          acknowledged: false
        },
        {
          alert_id: "2",
          patient_id: "P002",
          patient_name: "Mary Johnson",
          symptom_type: "dyspnea",
          alert_type: "rapid_increase",
          severity: "critical",
          current_score: 8,
          previous_scores: [3, 4, 5],
          trend_direction: "increasing",
          percentage_change: 60,
          message: "Dyspnea increased rapidly: 5 → 8 in single visit",
          recommended_action: "Urgent: Assess patient for respiratory distress.",
          created_at: new Date().toISOString(),
          acknowledged: false
        }
      ]);
    } finally {
      setAlertsLoading(false);
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await api.post(`/hope/trend-alerts/${alertId}/acknowledge`, {
        acknowledged_by: user?.email || "staff"
      });
      toast.success("Alert acknowledged");
      loadTrendAlerts();
      loadDashboardData();
    } catch (error) {
      toast.error("Failed to acknowledge alert");
    }
  };

  const handleDismissAlert = async (alertId) => {
    try {
      await api.delete(`/hope/trend-alerts/${alertId}`);
      toast.success("Alert dismissed");
      loadTrendAlerts();
      loadDashboardData();
    } catch (error) {
      toast.error("Failed to dismiss alert");
    }
  };

  const handleRunAnalysis = async () => {
    try {
      const response = await api.post("/hope/trend-alerts/analyze");
      toast.success(response.data.message || "Analysis complete");
      loadTrendAlerts();
      loadDashboardData();
    } catch (error) {
      toast.error("Analysis failed");
    }
  };

  const loadPatients = async () => {
    try {
      const response = await api.get("/ehr/patients/search?limit=100");
      setPatients(response.data.patients || []);
      if (response.data.patients?.length > 0) {
        setSelectedPatient(response.data.patients[0].id);
      }
    } catch (error) {
      console.error("Patients load error:", error);
      // Use mock patients
      setPatients([
        { id: "P001", name: "John Smith" },
        { id: "P002", name: "Mary Johnson" },
        { id: "P003", name: "Robert Williams" }
      ]);
      setSelectedPatient("P001");
    }
  };

  const loadPatientData = async (patientId) => {
    try {
      // Load latest observation
      const latestResponse = await api.get(`/hope/patient/${patientId}/latest`);
      setLatestObservation(latestResponse.data.observation);
      
      // Load trends
      const trendsResponse = await api.post("/hope/trends", {
        patient_id: patientId,
        days: 30
      });
      setTrendData(trendsResponse.data.trends || []);
    } catch (error) {
      console.error("Patient data load error:", error);
    }
  };

  const handleRecordingComplete = (result) => {
    setRecordingResult(result);
    setLatestObservation(result.observation);
    toast.success("Voice note processed! Review and verify the scores.");
    setActiveTab("verify");
  };

  const handleManualSubmit = async () => {
    if (!selectedPatient) {
      toast.error("Please select a patient");
      return;
    }

    try {
      const response = await api.post("/hope/manual-entry", {
        patient_id: selectedPatient,
        symptoms: manualScores,
        notes: manualNotes
      });
      
      toast.success("SOT scores saved successfully!");
      setLatestObservation(response.data.observation);
      loadDashboardData();
      loadPatientData(selectedPatient);
      
      // Reset form
      setManualScores(HOPE_SYMPTOMS.reduce((acc, s) => ({ ...acc, [s.key]: 0 }), {}));
      setManualNotes("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save scores");
    }
  };

  const handleVerifyObservation = async (corrections = null) => {
    if (!recordingResult?.observation?.id) {
      toast.error("No observation to verify");
      return;
    }

    try {
      await api.post(`/hope/verify/${recordingResult.observation.id}`, corrections);
      toast.success("Observation verified!");
      setRecordingResult(null);
      loadDashboardData();
      loadPatientData(selectedPatient);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Verification failed");
    }
  };

  const handleExportIQIES = async () => {
    try {
      const response = await api.post("/hope/export/iqies", {
        include_narratives: true
      });
      
      // Download as JSON file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `iqies_export_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${response.data.recordCount} records`);
    } catch (error) {
      toast.error("Export failed");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="hope-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-700 to-teal-600 text-white py-6 px-6">
        <div className="container-max">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold">HOPE Voice Pipeline</h1>
                <div className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full">
                  <StatusIndicator status={systemStatus} />
                  <span className="text-xs">{systemStatus === "operational" ? "All Systems Operational" : "Checking..."}</span>
                </div>
              </div>
              <p className="text-teal-100">
                Hospice Outcomes & Patient Evaluation - Voice-to-Data Pipeline
              </p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                onClick={handleExportIQIES}
              >
                <Download className="w-4 h-4 mr-2" />
                Export iQIES
              </Button>
              <Button 
                variant="outline"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                onClick={() => { loadDashboardData(); checkSystemStatus(); }}
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container-max px-6 py-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-100 rounded-lg">
                  <Activity className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboardSummary?.today_observations || 0}</p>
                  <p className="text-xs text-muted-foreground">Today's Observations</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={dashboardSummary?.pending_verification > 0 ? "border-amber-200 bg-amber-50" : ""}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${dashboardSummary?.pending_verification > 0 ? 'bg-amber-200' : 'bg-gray-100'}`}>
                  <Clock className={`w-5 h-5 ${dashboardSummary?.pending_verification > 0 ? 'text-amber-600' : 'text-gray-600'}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboardSummary?.pending_verification || 0}</p>
                  <p className="text-xs text-muted-foreground">Pending Verification</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={dashboardSummary?.high_symptom_alerts > 0 ? "border-red-200 bg-red-50" : ""}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${dashboardSummary?.high_symptom_alerts > 0 ? 'bg-red-200' : 'bg-gray-100'}`}>
                  <AlertTriangle className={`w-5 h-5 ${dashboardSummary?.high_symptom_alerts > 0 ? 'text-red-600' : 'text-gray-600'}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboardSummary?.high_symptom_alerts || 0}</p>
                  <p className="text-xs text-muted-foreground">High Symptom Alerts</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={trendAlerts.length > 0 ? "border-purple-200 bg-purple-50" : ""} onClick={() => setActiveTab("alerts")} style={{ cursor: "pointer" }}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${trendAlerts.length > 0 ? 'bg-purple-200' : 'bg-gray-100'}`}>
                  <BellRing className={`w-5 h-5 ${trendAlerts.length > 0 ? 'text-purple-600' : 'text-gray-600'}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{trendAlerts.length}</p>
                  <p className="text-xs text-muted-foreground">Trend Alerts</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <Users className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboardSummary?.patients_tracked || 0}</p>
                  <p className="text-xs text-muted-foreground">Patients Tracked</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Patient Selector */}
        <Card className="mb-6">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <Label className="font-medium">Select Patient:</Label>
              <Select value={selectedPatient} onValueChange={setSelectedPatient}>
                <SelectTrigger className="w-64" data-testid="patient-selector">
                  <SelectValue placeholder="Choose a patient" />
                </SelectTrigger>
                <SelectContent>
                  {patients.map(patient => (
                    <SelectItem key={patient.id} value={patient.id}>
                      {patient.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {latestObservation && (
                <Badge variant="outline" className="ml-auto">
                  Last observation: {new Date(latestObservation.visit_timestamp).toLocaleDateString()}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="record" className="gap-2">
              <Mic className="w-4 h-4" />
              Voice Record
            </TabsTrigger>
            <TabsTrigger value="manual" className="gap-2">
              <FileText className="w-4 h-4" />
              Manual Entry
            </TabsTrigger>
            <TabsTrigger value="trends" className="gap-2">
              <TrendingUp className="w-4 h-4" />
              SOT Trends
            </TabsTrigger>
            <TabsTrigger value="alerts" className="gap-2" data-testid="tab-alerts">
              <BellRing className="w-4 h-4" />
              Trend Alerts
              {trendAlerts.length > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">{trendAlerts.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="verify" className="gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Verify
              {recordingResult && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">1</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Voice Record Tab */}
          <TabsContent value="record">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Push-to-Talk */}
              <Card className="md:row-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mic className="w-5 h-5 text-teal-600" />
                    Push-to-Talk
                  </CardTitle>
                  <CardDescription>
                    Record clinical notes and automatically extract HOPE SOT scores
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col items-center py-8">
                  <PushToTalkButton
                    onRecordingComplete={handleRecordingComplete}
                    patientId={selectedPatient}
                    disabled={!selectedPatient}
                  />
                  
                  <div className="mt-8 p-4 bg-slate-100 rounded-lg w-full">
                    <h4 className="font-medium mb-2">Recording Tips:</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Mention specific symptom levels (e.g., "Pain level is 5")</li>
                      <li>• Use descriptors: none, slight, mild, moderate, severe, extreme</li>
                      <li>• Note any changes from previous visits</li>
                      <li>• Include PPS score if assessed</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Current Scores */}
              <Card>
                <CardHeader>
                  <CardTitle>Current SOT Scores</CardTitle>
                  <CardDescription>
                    {latestObservation 
                      ? `Last updated: ${new Date(latestObservation.visit_timestamp).toLocaleString()}`
                      : "No observations yet"}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {latestObservation ? (
                    <div className="grid grid-cols-2 gap-3">
                      {HOPE_SYMPTOMS.map(symptom => {
                        const symData = latestObservation.symptoms?.find(s => s.symptom_type === symptom.key);
                        return (
                          <SymptomScoreCard
                            key={symptom.key}
                            symptom={symptom}
                            score={symData?.impact_score || 0}
                            change={symData?.change_from_baseline}
                            baseline={symData?.baseline_score}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No observations recorded yet</p>
                      <p className="text-sm">Use Push-to-Talk or Manual Entry to record</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Manual Entry Tab */}
          <TabsContent value="manual">
            <Card>
              <CardHeader>
                <CardTitle>Manual SOT Entry</CardTitle>
                <CardDescription>
                  Manually enter symptom scores (0-9 scale)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
                  {HOPE_SYMPTOMS.map(symptom => (
                    <SymptomScoreCard
                      key={symptom.key}
                      symptom={symptom}
                      score={manualScores[symptom.key]}
                      editable={true}
                      onScoreChange={(key, val) => setManualScores(prev => ({ ...prev, [key]: val }))}
                    />
                  ))}
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="notes">Clinical Notes (Optional)</Label>
                    <Textarea
                      id="notes"
                      placeholder="Add any relevant clinical observations..."
                      value={manualNotes}
                      onChange={(e) => setManualNotes(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  
                  <Button onClick={handleManualSubmit} className="w-full bg-teal-600 hover:bg-teal-700">
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Save SOT Scores
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Symptom Outcome Trends</CardTitle>
                    <CardDescription>30-day HOPE SOT score history</CardDescription>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {HOPE_SYMPTOMS.map(symptom => (
                      <Button
                        key={symptom.key}
                        size="sm"
                        variant={selectedSymptoms.includes(symptom.key) ? "default" : "outline"}
                        onClick={() => {
                          setSelectedSymptoms(prev => 
                            prev.includes(symptom.key) 
                              ? prev.filter(s => s !== symptom.key)
                              : [...prev, symptom.key]
                          );
                        }}
                        style={selectedSymptoms.includes(symptom.key) ? { backgroundColor: symptom.color } : {}}
                      >
                        {symptom.label}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <SOTChart data={trendData} selectedSymptoms={selectedSymptoms} />
                
                {/* Symptom Averages */}
                <div className="mt-6 pt-6 border-t">
                  <h4 className="font-medium mb-4">7-Day Averages</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                    {HOPE_SYMPTOMS.map(symptom => {
                      const avg = dashboardSummary?.symptom_averages?.[symptom.key]?.average || 0;
                      return (
                        <div key={symptom.key} className="text-center p-3 bg-slate-50 rounded-lg">
                          <p className="text-xs text-muted-foreground mb-1">{symptom.label}</p>
                          <p className={`text-xl font-bold ${getScoreColor(Math.round(avg))}`}>
                            {avg.toFixed(1)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Verify Tab */}
          <TabsContent value="verify">
            <Card>
              <CardHeader>
                <CardTitle>Verify Observation</CardTitle>
                <CardDescription>
                  Review and verify AI-extracted HOPE scores
                </CardDescription>
              </CardHeader>
              <CardContent>
                {recordingResult ? (
                  <div className="space-y-6">
                    {/* Transcript */}
                    <div>
                      <Label className="mb-2 block">Transcript</Label>
                      <div className="p-4 bg-slate-50 rounded-lg text-sm">
                        {recordingResult.transcript}
                      </div>
                    </div>
                    
                    {/* AI-Extracted Narrative */}
                    {recordingResult.observation?.final_narrative && (
                      <div>
                        <Label className="mb-2 block">AI Summary</Label>
                        <div className="p-4 bg-teal-50 rounded-lg text-sm">
                          {recordingResult.observation.final_narrative}
                        </div>
                      </div>
                    )}
                    
                    {/* Scores */}
                    <div>
                      <Label className="mb-2 block">Extracted Scores (Click to Adjust)</Label>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {HOPE_SYMPTOMS.map(symptom => {
                          const symData = recordingResult.observation?.symptoms?.find(s => s.symptom_type === symptom.key);
                          return (
                            <SymptomScoreCard
                              key={symptom.key}
                              symptom={symptom}
                              score={symData?.impact_score || 0}
                              change={symData?.change_from_baseline}
                              baseline={symData?.baseline_score}
                            />
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Changes from Baseline */}
                    {recordingResult.changes_from_baseline?.length > 0 && (
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          Documentation by Exception - Changes Detected
                        </h4>
                        <ul className="text-sm text-amber-700 space-y-1">
                          {recordingResult.changes_from_baseline.map((change, idx) => (
                            <li key={idx}>
                              <strong>{change.symptom_type}</strong>: {change.baseline_score} → {change.impact_score} 
                              ({change.change_from_baseline > 0 ? '+' : ''}{change.change_from_baseline})
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Actions */}
                    <div className="flex gap-3">
                      <Button onClick={() => handleVerifyObservation()} className="flex-1 bg-teal-600 hover:bg-teal-700">
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Verify & Save
                      </Button>
                      <Button variant="outline" onClick={() => setRecordingResult(null)}>
                        Discard
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No pending observations to verify</p>
                    <p className="text-sm">Use Voice Record to create an observation</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Trend Alerts Tab */}
          <TabsContent value="alerts">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <BellRing className="w-5 h-5 text-purple-600" />
                      Automated Symptom Trend Alerts
                    </CardTitle>
                    <CardDescription>
                      AI-detected concerning symptom patterns requiring attention
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleRunAnalysis}>
                      <Zap className="w-4 h-4 mr-2" />
                      Run Analysis
                    </Button>
                    <Button variant="outline" size="sm" onClick={loadTrendAlerts}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {alertsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                  </div>
                ) : trendAlerts.length > 0 ? (
                  <div className="space-y-4">
                    {trendAlerts.map(alert => {
                      const symptomConfig = HOPE_SYMPTOMS.find(s => s.key === alert.symptom_type);
                      const Icon = symptomConfig?.icon || Activity;
                      
                      return (
                        <div 
                          key={alert.alert_id}
                          className={`p-4 rounded-lg border-l-4 ${
                            alert.severity === 'critical' 
                              ? 'bg-red-50 border-red-500' 
                              : 'bg-amber-50 border-amber-500'
                          }`}
                          data-testid={`alert-${alert.alert_id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`p-2 rounded-lg ${
                                alert.severity === 'critical' ? 'bg-red-200' : 'bg-amber-200'
                              }`}>
                                <Icon className={`w-5 h-5 ${
                                  alert.severity === 'critical' ? 'text-red-600' : 'text-amber-600'
                                }`} />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge variant={alert.severity === 'critical' ? 'destructive' : 'secondary'}>
                                    {alert.severity.toUpperCase()}
                                  </Badge>
                                  <Badge variant="outline">
                                    {alert.alert_type.replace('_', ' ').toUpperCase()}
                                  </Badge>
                                  <span className="text-sm text-muted-foreground">
                                    Patient: {alert.patient_name || alert.patient_id}
                                  </span>
                                </div>
                                <p className="font-medium text-gray-900">{alert.message}</p>
                                <div className="flex items-center gap-4 mt-2">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-muted-foreground">Trend:</span>
                                    <div className="flex items-center gap-1">
                                      {alert.previous_scores.map((score, idx) => (
                                        <span key={idx} className={`text-sm font-mono px-1.5 py-0.5 rounded ${getScoreColor(score)}`}>
                                          {score}
                                        </span>
                                      ))}
                                      <ArrowRight className="w-4 h-4 text-gray-400" />
                                      <span className={`text-sm font-mono px-1.5 py-0.5 rounded font-bold ${getScoreColor(alert.current_score)}`}>
                                        {alert.current_score}
                                      </span>
                                    </div>
                                  </div>
                                  {alert.percentage_change > 0 && (
                                    <Badge variant="outline" className="text-red-600">
                                      <ArrowUp className="w-3 h-3 mr-1" />
                                      {alert.percentage_change}%
                                    </Badge>
                                  )}
                                </div>
                                <div className="mt-3 p-3 bg-white rounded border">
                                  <p className="text-sm font-medium text-gray-700">Recommended Action:</p>
                                  <p className="text-sm text-gray-600">{alert.recommended_action}</p>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleAcknowledgeAlert(alert.alert_id)}
                                className="gap-1"
                              >
                                <Eye className="w-4 h-4" />
                                Acknowledge
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => handleDismissAlert(alert.alert_id)}
                                className="gap-1 text-gray-500"
                              >
                                <X className="w-4 h-4" />
                                Dismiss
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-500" />
                    <p className="font-medium text-green-700">No Active Trend Alerts</p>
                    <p className="text-sm">All patients' symptoms are within normal parameters</p>
                    <Button variant="outline" className="mt-4" onClick={handleRunAnalysis}>
                      <Zap className="w-4 h-4 mr-2" />
                      Run Analysis Now
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
